import type {PropsWithChildren} from 'react'
import {createContext, use} from 'react'

/**
 * Per-row selection state. Extracted from `DiffLineContext` because
 * `isRowSelected` toggles independently on every user selection click,
 * while the rest of the per-row data (diffLine, rowId, hunk refs, etc.)
 * is set together when the row is constructed for its position and does
 * not change in response to selection toggles.
 *
 * Splitting lets ~10 line-level consumers that don't care about selection
 * skip re-rendering when the user drags a selection across rows.
 */
const DiffLineSelectionContext = createContext<boolean>(false)

export function DiffLineSelectionContextProvider({
  children,
  isRowSelected,
}: PropsWithChildren<{isRowSelected: boolean}>) {
  return <DiffLineSelectionContext value={isRowSelected}>{children}</DiffLineSelectionContext>
}

/**
 * Subscribe only to the per-row selection flag. Use this instead of
 * `useDiffLineContext().isRowSelected` — `useDiffLineContext()` no longer
 * exposes selection state, and the dedicated hook avoids re-rendering on
 * line-data changes you do not consume.
 */
export function useDiffLineSelected(): boolean {
  return use(DiffLineSelectionContext)
}
